import { alertAIApiUrl } from "@/config/endpoints";
import { auth } from "@/config/firebaseApp";
import { useEffect, useState } from "react";
import { io, Socket } from "socket.io-client";

/**
 * La conexión de socket del portal, autenticada con el token de Firebase.
 *
 * ## Por qué espera a la sesión en vez de leerla una vez
 *
 * Antes esto hacía `await auth?.currentUser?.getIdToken()` una sola vez al
 * montar. En el hotfix de la fuga del login (#756) le agregué un
 * `if (!token) return`, y eso introdujo una regresión: el efecto corre una vez
 * y **no tiene dependencias**, así que si Firebase todavía no restauró la
 * sesión en ese instante, `currentUser` es `null`, el token viene `undefined`
 * y **el socket no se conecta nunca más**. No hay reintento.
 *
 * El síntoma es intermitente y difícil de atribuir: al enfermero no le llega el
 * aviso de que el médico lo está llamando, pero sólo cuando la página se cargó
 * antes de que Firebase resolviera. Reportado el 10-sep desde dev.
 *
 * `onIdTokenChanged` resuelve las dos cosas: dispara cuando la sesión se
 * restaura —así que no hay carrera— y también en cada renovación de token, lo
 * que además arregla algo que ya estaba mal antes de todo esto: el socket se
 * quedaba con el token del arranque y no se enteraba de las renovaciones.
 *
 * Y sigue sin conectar sin sesión, que es lo que #756 buscaba: sin usuario no
 * se abre socket, y si la sesión se cierra el que había se desconecta.
 */
export function useSocket() {
	const [socket, setSocket] = useState<Socket | null>(null);

	useEffect(() => {
		let conexion: Socket | null = null;
		let desmontado = false;

		const desuscribir = auth.onIdTokenChanged(async (usuario) => {
			if (desmontado) return;

			// Sin sesión no hay socket. Si había uno, se cierra.
			if (!usuario) {
				conexion?.disconnect();
				conexion = null;
				setSocket(null);
				return;
			}

			/*
			 * Ya hay una conexión viva: no se reconecta.
			 *
			 * `onIdTokenChanged` también dispara en cada renovación de token —el
			 * SDK renueva solo cada hora—, y reconectar ahí tiraría abajo un
			 * socket que está funcionando, justo en medio de una atención.
			 */
			if (conexion) return;

			try {
				const nueva = io(String(alertAIApiUrl), {
					/**
					 * Sólo WebSocket. El respaldo por HTTP long-polling se saca.
					 *
					 * El polling crea un `sid` que vive en la memoria de UNA
					 * instancia del backend. Cloud Run no da afinidad de sesión
					 * garantizada, así que los pedidos siguientes caen en otra
					 * instancia que no conoce ese `sid` y responden 400 «Session
					 * ID unknown». La documentación de socket.io nombra el error
					 * y da como única causa la falta de sticky sessions.
					 *
					 * En Cloud Run no se puede cumplir: Google documenta su
					 * afinidad como *best effort* y avisa que "you cannot assume
					 * that a client will always reconnect to the same instance,
					 * even when session affinity is enabled". Se rompe cuando una
					 * instancia termina o llega a su límite de concurrencia o CPU.
					 *
					 * Antes se dejaba el respaldo para no dejar sin conexión a las
					 * redes que bloquean WebSocket. Medido en prod el 11-sep, ese
					 * respaldo ya no respalda: de 415 sesiones que cayeron a
					 * polling, 341 nunca lograron subir a WebSocket, y de ésas
					 * **118 no funcionaron ni una vez**. En total esas sesiones
					 * hicieron 299 pedidos con 200 contra 747 con 400: fallan 7 de
					 * cada 10.
					 *
					 * O sea que no estamos sacando algo que funciona. Quien no
					 * pueda abrir un WebSocket se queda sin avisos en tiempo real
					 * —la app sigue andando por HTTP— en vez de con una conexión
					 * que falla el 71% de las veces y llena los logs.
					 */
					transports: ["websocket"],
					/*
					 * `auth` como función y no como objeto: socket.io la ejecuta
					 * en **cada** intento de conexión, incluidas las
					 * reconexiones, así que el token siempre va fresco.
					 *
					 * Con un objeto, el token queda fijado al momento de crear el
					 * socket. Los de Firebase duran una hora: una reconexión
					 * después de eso iría con uno vencido. Es el patrón que ya
					 * usa la app del médico.
					 */
					auth: async (cb: (datos: { token: string }) => void) => {
						const token = await auth.currentUser?.getIdToken();
						cb({ token: `Bearer ${token}` });
					},
				});

				nueva.connect();
				conexion = nueva;
				setSocket(nueva);
			} catch (error) {
				console.error("Error initializing socket:", error);
			}
		});

		return () => {
			desmontado = true;
			desuscribir();
			conexion?.disconnect();
		};
	}, []);

	return socket;
}
